import Grid from '@mui/material/Grid'
import Fab from '@mui/material/Fab';
import Save from '@mui/icons-material/Save';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import CandlestickChartRoundedIcon from '@mui/icons-material/CandlestickChartRounded';
import { alpha, useTheme } from '@mui/material/styles';

import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../interface/ApplicationState';
import { clearUpdateUserProfile, getUserBegin, setUserBegin } from '../redux/actions/userActions';

import { User } from '../interface/User';
import { Indicators } from '../components/Indicators';
import { useEffect } from 'react';
import { AICheckerSettings, supportsAI } from './settings/AICheckerSettings';
import { TradeSettings } from './settings/TradeSettings';
import { LossPrevention } from './settings/LossPrevention';
import { ProfitConfiguration } from './settings/ProfitConfiguration';
import { BinanceConnectionSettings } from './settings/BinanceConnectionSettings';
import { CandleStickSettings } from './settings/CandleStickSettings';
import { SignalsSection } from './signals/SignalsSection';
import { SubscriptionSection } from '../components/subscriptions/SubscriptionSection';
import { TradingPair } from '../interface/TradingPair';
import { GateIoConnectionSettings } from './settings/GateIoConnectionSettings';
import { ExchangeSelection } from './settings/ExchangeSelection';
import { BotExchange } from '../interface/BotExchange';
import { KrakenConnectionSettings } from './settings/KrakenConnectionSettings';
import { CoinbaseConnectorSettings } from './settings/CoinbaseConnectorSettings';

export const Settings = () =>
{
  const theme = useTheme()
  const dispatch = useDispatch()
  const isGettingUserProfile = useSelector((state: ApplicationState) => state.userStore.isGettingUserProfile)
  const isSettingUserProfile = useSelector((state: ApplicationState) => state.userStore.isSettingUserProfile)
  const updateProfileResult = useSelector((state: ApplicationState) => state.userStore.updateProfileResult)

  const user = useSelector((state: ApplicationState) => state.userStore.user)

  const isAtLeastOneIndicatorEnabled = () =>
  {
    if(!user.tradingEnabled || user.isAIEnabled) return true
    if(user.isDynamicEnabled) return true
    if(user.macdEnabled) return true
    if(user.rsiEnabled) return true
    if(user.candleStickPatternEnabled) return true
    if(user.emaEnabled) return true
    if(user.tripleEmaEnabled) return true
    if(user.mfiEnabled) return true
    if(user.customEnabled) return true
    if(user.smaEnabled) return true
    if(user.rsiCrossOverEnabled) return true
    if(user.isTripleRSIEnabled) return true
    if(user.isVolumeProfileEnabled) return true
    if(user.isVolumeCrossoverEnabled) return true
    if(user.isObvEnabled) return true
    if(user?.smartMoney?.isSmartMoneyEnabled) return true

    return false
  }

  const isTradingPairsValid = () =>
  {
    if(user.isAIEnabled || !user.tradingEnabled) return true
    if(!user.tradingPairs) return true
    const dups: TradingPair[] = []
    for(const pair of user.tradingPairs)
    {
      if(!pair.base || !pair.maxInvest || !pair.pair) return false
      if(dups.find(p => p.pair === pair.pair)) return false
      dups.push(pair);
    }

    return true
  }

  const isAIConfigurationValid = !user.isAIEnabled || !user.tradingEnabled ||
    (supportsAI(user.botExchange) && !!user.fundingCurrency &&
      Number.isFinite(Number(user.maxBuy)) && Number(user.maxBuy) > 0 &&
      Number.isFinite(Number(user.maxSell)) && Number(user.maxSell) > 0)

  const updateUser = () =>
  {
    const updatedUser: User = {
      tradingEnabled: user.tradingEnabled,
      isAIEnabled: !!user.isAIEnabled,
      chartInterval: user.chartInterval,
      binanceApiKey: user.binanceApiKey,
      binanceApiSecret: user.binanceApiSecret,
      maxBuy: user.maxBuy,
      maxSell: user.maxSell,
      tradingPair: user.tradingPair,
      fundingCurrency: user.fundingCurrency,
      enableLong: user.enableLong,
      macdBuyThreshold: user.macdBuyThreshold,
      macdSellThreshold: user.macdSellThreshold,
      macdEnabled: user.macdEnabled,
      candleStickPatternEnabled: user.candleStickPatternEnabled,
      rsiEnabled: user.rsiEnabled,
      rsiThresholdBuy: user.rsiThresholdBuy,
      rsiThresholdSell: user.rsiThresholdSell,
      takeProfitThreshold: user.takeProfitThreshold,
      isMinSellProfitEnabled: user.isMinSellProfitEnabled,
      emaEnabled: user.emaEnabled,
      takeProfitPercentage: user.takeProfitPercentage,
      isTakeProfitEnabled: user.isTakeProfitEnabled,
      mfiEnabled: user.mfiEnabled,
      mfiThresholdBuy: user.mfiThresholdBuy,
      mfiThresholdSell: user.mfiThresholdSell,
      customEnabled: user.customEnabled,
      smaEnabled: user.smaEnabled,
      emaShortPeriod: user.emaShortPeriod,
      emaLongPeriod: user.emaLongPeriod,
      emaType: user.emaType,
      tripleEmaEnabled: user.tripleEmaEnabled,
      tripleEmaShortPeriod: user.tripleEmaShortPeriod,
      tripleEmaMediumPeriod: user.tripleEmaMediumPeriod,
      tripleEmaLongPeriod: user.tripleEmaLongPeriod,
      sellProfitsThreshold: user.sellProfitsThreshold,
      sellProfitsToFiat: user.sellProfitsToFiat,
      isStopLossEnabled: user.isStopLossEnabled,
      stopLossPercentage: user.stopLossPercentage,
      isSignalsEnabled: user.isSignalsEnabled,
      signalsInvestPercent: user.signalsInvestPercent,
      signalsInvestSources: user.signalsInvestSources,
      signalsMaxBuy: user.signalsMaxBuy,
      isAdxEnabled: user.isAdxEnabled,
      adxTrendLimit: user.adxTrendLimit,
      isBuyBackInDelayEnabled: user.isBuyBackInDelayEnabled,
      buyBackInDelay: user.buyBackInDelay,
      isDynamicEnabled: user.isDynamicEnabled,
      switchToTrendingIndicatorThreshold: user.switchToTrendingIndicatorThreshold,
      currentIndicator: user.currentIndicator,
      preferMarket: user.preferMarket,
      rsiCrossOverEnabled: user.rsiCrossOverEnabled,
      rsiShort: user.rsiShort,
      rsiMedium: user.rsiMedium,
      rsiLong: user.rsiLong,
      binanceApiEndpoint: user.binanceApiEndpoint,
      isTripleRSIEnabled: user.isTripleRSIEnabled,
      tripleRsi1: user.tripleRsi1,
      tripleRsi2: user.tripleRsi2,
      tripleRsi3: user.tripleRsi3,
      tradingPairs: user.tradingPairs,
      isMultiTradingEnabled: user.isMultiTradingEnabled,
      isVolumeProfileEnabled: user.isVolumeProfileEnabled,
      vpNumberOfBars: user.vpNumberOfBars,
      isVolumeCrossoverEnabled: user.isVolumeCrossoverEnabled,
      shortVolumeCrossover: user.shortVolumeCrossover,
      longVolumeCrossover: user.longVolumeCrossover,
      enableGrid: user.enableGrid,
      isObvEnabled: user.isObvEnabled,
      obvShortPeriod: user.obvShortPeriod,
      obvLongPeriod: user.obvLongPeriod,
      buyOnlyIfChangePositive: user.buyOnlyIfChangePositive,
      botExchange: user.botExchange,
      gateIoApiKey: user.gateIoApiKey,
      gateIoApiSecret: user.gateIoApiSecret,
      krakenApiKey: user.krakenApiKey,
      krakenApiSecret: user.krakenApiSecret,
      coinbaseApiKey: user.coinbaseApiKey,
      coinBaseApiSecret: user.coinBaseApiSecret,
      smartMoney: user?.smartMoney
    }

    dispatch(setUserBegin(updatedUser))
  }

  useEffect(() => {

    dispatch(getUserBegin())

  }, [])

  const sectionShell = (title: string, helper: string, content: React.ReactNode) => (
    <Grid item xs={12}>
      <Box sx={panelStyles.sectionShell(theme)}>
        <Stack spacing={0.35} sx={{ mb: 1.25 }}>
          <Typography variant="overline" sx={panelStyles.sectionEyebrow(theme)}>{title}</Typography>
          <Typography variant="h5" sx={panelStyles.sectionTitle}>{title}</Typography>
          <Typography variant="body2" sx={panelStyles.sectionHelper(theme)}>{helper}</Typography>
        </Stack>
        {content}
      </Box>
    </Grid>
  )

  return(
    <Box sx={panelStyles.page(theme)}>
      <Box sx={panelStyles.container}>

      <CssBaseline />
      <Grid container spacing={1.5}>

      <Grid item xs={12}>
        <Box sx={panelStyles.hero(theme)}>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} justifyContent="space-between">
            <Box>
              <Typography variant="overline" sx={panelStyles.heroEyebrow(theme)}>Bot Control Center</Typography>
              <Typography variant="h3" sx={panelStyles.heroTitle}>Settings</Typography>
              <Typography variant="body1" sx={panelStyles.heroCopy(theme)}>
                Tune trading behavior, connect exchanges, manage protection rules, and keep your live bot setup aligned with your strategy.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip icon={<TuneRoundedIcon />} label={user.botExchange || "Exchange"} sx={panelStyles.heroChip(theme)} />
              <Chip icon={<AutoGraphRoundedIcon />} label={user.isSignalsEnabled ? "Signals enabled" : "Signals off"} sx={panelStyles.heroChip(theme)} />
              <Chip icon={<ShieldRoundedIcon />} label={user.tradingEnabled ? "Trading active" : "Trading paused"} sx={panelStyles.heroChip(theme)} />
              <Chip icon={<CandlestickChartRoundedIcon />} label={user.isAIEnabled ? "AI · hourly decisions" : `Interval ${user.chartInterval || "1m"}`} sx={panelStyles.heroChip(theme)} />
            </Stack>
          </Stack>
        </Box>
      </Grid>

      {sectionShell("Subscription", "Check membership status, renewal timing, and plan access.", <SubscriptionSection />)}

      {sectionShell("Exchange Selection", "Choose the venue your bot will execute against and keep credentials aligned with that exchange.", <ExchangeSelection />)}

      { user.botExchange === BotExchange.gateIo && sectionShell("Gate.io Connection Settings", "Review the API credentials and connection details used by your bot.", <GateIoConnectionSettings />) }

      { user.botExchange === BotExchange.kraken && sectionShell("Kraken Connection Settings", "Review the API credentials and connection details used by your bot.", <KrakenConnectionSettings />) }

      { user.botExchange === BotExchange.coinbase && sectionShell("Coinbase Connection Settings", "Review the API credentials and connection details used by your bot.", <CoinbaseConnectorSettings />) }

      { (user.botExchange === BotExchange.binance || user.botExchange === BotExchange.binanceTestnet || user.botExchange === BotExchange.binanceUS) &&
        sectionShell(`${user.botExchange} Connection Settings`, "Review the API credentials and endpoint configuration used by your bot.", <BinanceConnectionSettings />) }

      {sectionShell("AIChecker", "Let AI review markets and make an hourly trading decision.", <AICheckerSettings />)}

      {sectionShell("Trading Settings", "Control execution mode, pair selection, capital limits, and whether the bot trades one market or many.", <TradeSettings />)}

      {!user.isAIEnabled && sectionShell("Loss Prevention", "Define the downside rules that stop weak exits and protect the account from poor re-entry timing.", <LossPrevention />)}

      {!user.isAIEnabled && sectionShell("Profit Configuration", "Set how profits are locked in and whether gains are converted back to your base currency.", <ProfitConfiguration />)}

      {!user.isAIEnabled && sectionShell("Signals", "Control Telegram signal participation and define how much capital can be allocated to signal-driven trades.", <SignalsSection />)}

      <Grid item xs={12}>
        <Box sx={panelStyles.sectionShell(theme)}>
          {user.isAIEnabled
            ? <Alert severity="info">AIChecker chooses its own technical indicators. Your existing indicator settings are preserved and will apply again when AI is disabled and saved.</Alert>
            : <Indicators />}
          {!isAIConfigurationValid && <Alert severity="error" sx={{ mt: 1 }}>Choose a supported Binance exchange, a funding currency, and positive maximum buy and sell amounts before saving.</Alert>}
        </Box>
      </Grid>

      { !user.isAIEnabled && !user.isMultiTradingEnabled &&
        sectionShell("Candle Stick Chart Interval", "Choose the candle grouping that best matches your trading horizon and sensitivity.", <CandleStickSettings />)}

  </Grid>

 </Box>

    <Fab disabled={isGettingUserProfile || isSettingUserProfile || !isAtLeastOneIndicatorEnabled() || !isTradingPairsValid() || !isAIConfigurationValid } color="primary" aria-label="save" style={styles.fab} onClick={updateUser}>
    { isGettingUserProfile || isSettingUserProfile ? <CircularProgress /> : <Save /> }
    </Fab>

    <Snackbar open={!!updateProfileResult} onClose={() => dispatch(clearUpdateUserProfile())} autoHideDuration={3000}>
        <Alert severity={updateProfileResult === "Successfully updated profile" ? "success" : "error"} onClose={() => dispatch(clearUpdateUserProfile())}>
        {updateProfileResult}
      </Alert>
    </Snackbar>
  </Box>
  )
}

const panelStyles = {
  page: (theme: any) => ({
    minHeight: "100%",
    background:
      theme.palette.mode === "dark"
        ? `radial-gradient(circle at top, ${alpha("#123457", 0.28)} 0%, transparent 42%), linear-gradient(180deg, #08111d 0%, #0b1422 100%)`
        : `radial-gradient(circle at top, ${alpha("#60b8ff", 0.18)} 0%, transparent 42%), linear-gradient(180deg, #f4f8fc 0%, #eaf1fb 100%)`,
  }),
  container: {
    margin: 0,
    padding: 0,
  },
  hero: (theme: any) => ({
    p: { xs: 1.25, md: 1.5 },
    borderRadius: "16px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.62)}`,
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(135deg, ${alpha("#10233d", 0.96)} 0%, ${alpha("#0d1827", 0.94)} 52%, ${alpha("#12334a", 0.88)} 100%)`
        : `linear-gradient(135deg, ${alpha("#ffffff", 0.96)} 0%, ${alpha("#eef6ff", 0.96)} 52%, ${alpha("#d8ebff", 0.92)} 100%)`,
    boxShadow: theme.palette.mode === "dark" ? "0 24px 60px rgba(0,0,0,0.28)" : "0 18px 42px rgba(79, 131, 196, 0.16)",
  }),
  heroEyebrow: (theme: any) => ({
    letterSpacing: 1.6,
    color: alpha(theme.palette.text.primary, 0.66),
  }),
  heroTitle: {
    fontWeight: 700,
    lineHeight: 1.05,
    mb: 0.35,
  },
  heroCopy: (theme: any) => ({
    maxWidth: 760,
    color: alpha(theme.palette.text.primary, 0.74),
  }),
  heroChip: (theme: any) => ({
    height: 34,
    borderRadius: "10px",
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.7),
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.55)}`,
    '& .MuiChip-label': {
      fontWeight: 600,
    },
  }),
  sectionShell: (theme: any) => ({
    p: { xs: 1, md: 1.25 },
    borderRadius: "16px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.54),
    boxShadow: theme.palette.mode === "dark" ? "0 16px 38px rgba(0,0,0,0.16)" : "0 16px 36px rgba(117, 150, 191, 0.12)",
  }),
  sectionEyebrow: (theme: any) => ({
    letterSpacing: 1.5,
    color: alpha(theme.palette.text.primary, 0.6),
  }),
  sectionTitle: {
    fontWeight: 700,
  },
  sectionHelper: (theme: any) => ({
    color: alpha(theme.palette.text.primary, 0.68),
    maxWidth: 760,
  }),
}

export const styles = {
  container: {
    margin: 16,
    padding: 16,
    overflow: "auto",
    height: "90vh"
  },
  slider: {
    marginTop: 30,
  },
  heading: {
    fontWeight: "600"
  },
  fab: {
    position: "fixed" as "fixed",
    right: 32,
    bottom: 32,
    margin: 0,
    zIndex: 1200,
  },
  spacer: {
    marginBottom: 12,
  },
  card: {
    padding: 32,
    marginTop: 16,
    marginRight: 32,
    width: 300,
  },
  formLabel: {
    marginBottom: 12
  }
}
